import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { secureGet } from "../config/api";
import { useTranslation } from "react-i18next";

import DynamicForm from "./DynamicForm";
import ProductForm from "../modules/Products/ProductForm";
import ClientForm from "../modules/Clients/ClientForm";
import PurchaseForm from "../modules/Purchases/PurchaseForm";
import OrderForm from "../modules/Orders/OrderForm";
import RequestForm from "../modules/Repo/RequestForm";
import CreditNoteForm from "../modules/CreditNotes/CreditNotesForm";
import AvoirFinancierForm from "../modules/AvoirFinanciers/AvoirFinancierForm";
import AvoirFournisseurForm from "../modules/AvoirFournisseurs/AvoirFournisseurForm";
import PaymentsForm from "../modules/Recouvrement/PaymentsForm";
import ExpenseForm from "../modules/Performance/ExpenseForm";
import QuoteForm from "../modules/Quotes/QuoteForm";
import { buildPaymentOptionsFromServer } from "../utils/paymentModes";
import { buildOptionsFromFrenchRows } from "../utils/paymentModesFr";
import imageIcon from "../icons/image.svg";
import SnackbarNotification from './SnackbarNotification';

function ModuleWrapper({ moduleType, mode = "add", onClose, onSuccess, initialValues: propInitialValues }) {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const abortController = useMemo(() => new AbortController(), []);
  const signal = abortController.signal;
  const getStoredValues = (key) => {
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData) : {};
  };

  const [initialValues,] = useState(() => {
    if (moduleType === "settings") {
      return getStoredValues(moduleType);
    } else if (moduleType === "profile") {
      return getStoredValues("user");
    }
    return location.state?.initialValues || propInitialValues || {};
  });

  const getFormTitle = () => {
    return t(`form_titles.${mode === "modify" ? `edit_${moduleType}` : `add_${moduleType}`}`);
  };

  const [, setLoading] = useState(false);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };
  
  const [dropdownOptions, setDropdownOptions] = useState({
    clients: [],
    repos: [],
    paymentMode: [],
    parentId: [],
    providers: [],
    products: [],
    families: [],
    brands: [],
    roles: [],
    cities: [],
    banks: [],
    expense_category: [],
    commercial: [],
  });



  useEffect(() => {
    const fetchAdditionalFields = async () => {
      try {
        let url;
        switch (moduleType) {
          case "brands":
            url = "/config/brand/fields";
            break;
          case "families":
            url = "/config/family/fields";
            break;
          case "products":
            url = "/config/product/fields";
            break;
          case "clients":
            url = "/config/client/fields";
            break;
          case "orders":
            url = "/config/cart/fields";
            break;
          case "purchases":
            url = "/config/purchase/fields";
            break;
          case "providers":
            url = "/config/provider/fields";
            break;

          default:
            console.warn(`No endpoint configured for moduleType: ${moduleType}`);
            setAdditionalFields([]);
            return;
        }

        const response = await secureGet(url, showSnackbar, { signal });

        if (response && response.status === 200) {
          const visibleFields = response.data;

          const fields = visibleFields.map((field) => ({
            name: field.name,
            label: field.label || field.name,
            type: field.type?.values?.length > 0 ? "dropdown" : "text",
            required: field.mandatory,
            options: field.type?.values?.map((value) => ({
              value: value,
              label: value,
            })) || [],
          }));

          setAdditionalFields(fields);
        }
        else if (response && response.status === 404) {
          console.warn(`No additional fields exist for ${moduleType}`);
          setAdditionalFields([]);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
        console.error(`Error fetching additional fields for ${moduleType}:`, error);
        setAdditionalFields([]);
      }
    };

    fetchAdditionalFields();
  }, [moduleType, signal]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const options = {};

        if (moduleType === "families") {
          const { data: familiesData } = await secureGet("/families", showSnackbar, { signal });

          options.parentId = [
            ...familiesData.map((family) => ({
              value: family.id,
              label: family.name,
            })),
          ];
        }

        if (moduleType === "purchases") {
          const { data: providersData } = await secureGet("/providers", showSnackbar, { signal });
          options.providers = providersData.map((provider) => ({
            value: provider.id,
            label: provider.name,
          }));

          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const products = await secureGet("/products/unpaginated", showSnackbar, { signal });
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));
        }

        if (moduleType === "orders") {
          const { data: clientsData } = await secureGet("/clients", showSnackbar, { signal });
          options.clients = clientsData.map((client) => {
            const encoursMaxField = client.encoursMaximum;
            return {
              value: client.id,
              label: client.name,
              reference: client.code,
              name: client.name,
              encoursMax: encoursMaxField,
            };
          });

          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const { data: paymentData } = await secureGet("/payments/types", showSnackbar, { signal });
          const lang = i18n.resolvedLanguage;
          options.paymentMode = buildPaymentOptionsFromServer(paymentData, {
            t,
            lang,
          });

          const products = await secureGet("/products/unpaginated", showSnackbar, { signal });
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));
        }

        if (moduleType === "quotes") {
          const { data: clientsData } = await secureGet("/clients", showSnackbar, { signal });
          options.clients = clientsData.map((client) => {
            const encoursMaxField = client.encoursMaximum;
            return {
              value: client.id,
              label: client.name,
              reference: client.code,
              name: client.name,
              encoursMax: encoursMaxField ? encoursMaxField.value : null,
            };
          });

          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const { data: paymentData } = await secureGet("/payments/types", showSnackbar, { signal });
          const lang = i18n.resolvedLanguage;
          options.paymentMode = buildPaymentOptionsFromServer(paymentData, {
            t,
            lang,
          });

          const products = await secureGet("/products/unpaginated", showSnackbar, { signal });
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));
        }

        if (moduleType === "payments") {
          const { data: clientsData } = await secureGet("/clients", showSnackbar, { signal });
          options.clients = clientsData.map((client) => {
            return {
              value: client.id,
              label: client.name,
              reference: client.code,
              name: client.name,
            };
          });

          const { data: paymentData } = await secureGet("/payments/types", showSnackbar, { signal });
          options.paymentMode = paymentData.map((paymentMode) => ({
            value: paymentMode.id,
            label: paymentMode.name,
          }));


          const products = await secureGet("/banks", showSnackbar, { signal });
          options.banks = products.data.map((bank) => ({
            value: bank.id,
            label: bank.name,
          }));
        }

        if (moduleType === "creditNotes") {
          const { data: clientsData } = await secureGet("/clients", showSnackbar, { signal });
          options.clients = clientsData.map((client) => {
            return {
              value: client.id,
              label: client.name,
              reference: client.code,
              name: client.name,
            };
          });

          const products = await secureGet("/products/unpaginated", showSnackbar, { signal });
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));
        }

        if (moduleType === "products") {
          const { data: familiesData } = await secureGet("/families", showSnackbar, { signal });
          options.families = familiesData.map((family) => ({
            value: family.id,
            label: family.name,
          }));

          const { data: brandsData } = await secureGet("/brands", showSnackbar, { signal });
          options.brands = brandsData.map((brand) => ({
            value: brand.id,
            label: brand.name,
          }));
        }

        if (moduleType === "requests") {
          const { data: sourceReposData } = await secureGet("/repos", showSnackbar, { signal });
          options.sourceRepos = sourceReposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const { data: destReposData } = await secureGet("/repos", showSnackbar, { signal });
          options.destRepos = destReposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const products = await secureGet("/products/unpaginated", showSnackbar, { signal });
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));
        }

        if (moduleType === "users") {
          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
            active: repo.active,
          }));

          const roleKeys = Object.keys(t("roles", { returnObjects: true }) || {});
          options.roles = roleKeys.map((roleKey) => ({
            value: roleKey,
            label: t(`roles.${roleKey}`),
          }));
        }

        if (moduleType === "clients") {
          const { data: paymentData } = await secureGet("/payments/types", showSnackbar, { signal });
          const lang = i18n.resolvedLanguage;
          options.paymentMode = buildOptionsFromFrenchRows(paymentData, {
            t,
            lang,
          });

          const { data: citiesData } = await secureGet("/cities", showSnackbar, { signal });
          options.cities = citiesData.map((city) => ({
            value: city.id,
            label: city.name,
          }));
        }

        if (moduleType === "expenses") {
          const expenses = Object.keys(t("expense_types", { returnObjects: true }) || {});
          options.expense_category = expenses.map((roleKey) => ({
            value: roleKey,
            label: t(`expense_types.${roleKey}`),
          }));
        }

        if (moduleType === "afournisseur") {
          const { data: sourceReposData } = await secureGet("/repos", showSnackbar, { signal });
          options.sourceRepos = sourceReposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const products = await secureGet("/products/unpaginated", showSnackbar, { signal });
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));

          const { data: providersData } = await secureGet("/providers", showSnackbar, { signal });
          options.providers = providersData.map((provider) => ({
            value: provider.id,
            label: provider.name,
          }));
        }

        if (moduleType === "afinancier") {
          const { data: clientsData } = await secureGet("/clients", showSnackbar, { signal });
          options.clients = clientsData.map((client) => {
            return {
              value: client.id,
              label: client.name,
              reference: client.code,
              name: client.name,
            };
          });

          const { data: commercialData } = await secureGet("/users/ROLE_COMMERCIAL", showSnackbar, { signal });
          options.commercial = commercialData.map((commercial) => ({
            value: commercial.id,
            label: `${commercial.firstName} ${commercial.lastName}`,
          }));
        }

        setDropdownOptions((prevOptions) => ({ ...prevOptions, ...options }));
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };
    fetchOptions();

    return () => {
      abortController.abort();
    };
  }, [moduleType, signal, t, abortController]);

  const getProductFormFields = () => {
    return {
      baseFields: [
        { name: "name", label: t(`fields.products.name`), required: true },
        { name: "reference", label: t(`fields.products.reference`), required: true },
        { name: "priceHT", label: t(`fields.products.priceHT`), type: "text", required: true },
        { name: "priceTTC", label: t(`fields.products.priceTTC`), type: "text", required: true },
        { name: "discount", label: t(`fields.products.discount`), type: "number" },
        { name: "salesDiscountLimit", label: t(`fields.products.salesDiscountLimit`), type: "number" },
      ],
      familyAndBrandFields: [
        {
          name: "familyId",
          label: t(`fields.products.familyId`),
          type: "dropdown",
          options: dropdownOptions.families,
          required: true,
        },
        {
          name: "brandId",
          label: t(`fields.products.brandId`),
          type: "dropdown",
          options: dropdownOptions.brands,
          required: true,
        },
      ],
      quantityFields: [
        { name: "quantity", label: t(`fields.products.quantity`), type: "number" },
        { name: "price", label: t(`fields.products.price`)},
      ],
      fileFields: [
        {
          name: "logo",
          label: t(`fields.products.logo`),
          icon: <img src={imageIcon} alt='product logo' />,
          accept: "image/*",
          placeholderText1: t(`placeholder.upload_logo1`),
          placeholderText2: t(`placeholder.upload_logo2`),
        },
        // {
        //   name: "productSheet",
        //   label: t(`fields.products.productSheet`),
        //   icon: <img src={pdfIcon} alt="product sheet" />,
        //   accept: "application/pdf",
        //   placeholderText1: t(`placeholder.upload_product_sheet1`),
        //   placeholderText2: t(`placeholder.upload_product_sheet2`),
        // },
      ],
    };
  };

  const getClientFormFields = () => {
    return {
      baseFields: [
        { name: "name", label: t(`fields.clients.name`), required: true },
        { name: "code", label: t(`fields.clients.code`), required: true },
        { name: "cityId", label: t(`fields.clients.cityId`), type: "dropdown", options: dropdownOptions.cities, required: true },
        { name: "address", label: t(`fields.clients.address`) },
        { name: "taxId", label: t(`fields.clients.taxId`) },
        { name: "contactPrincipal", label: t(`fields.clients.contactPrincipal`) },
        { name: "phone", label: t(`fields.clients.phone`) },
      ],
    };
  };

  const getFieldSpecs = () => {
    switch (moduleType) {
      case "brands":
        return [
          { label: t(`fields.brands.name`), name: "name", required: true },
        ];
      case "families":
        return [
          { label: t(`fields.families.name`), name: "name", required: true },
          { label: t(`fields.families.parentId`), name: "parentId", type: "dropdown", options: dropdownOptions.parentId },
        ];
      case "orders":
        return [
          { label: t(`fields.orders.clientId`), name: "clientId", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`fields.orders.repoId`), name: "repoId", type: "dropdown", required: true, options: dropdownOptions.repos },
          { label: t(`fields.orders.paymentMode`), name: "paymentMode", type: "dropdown", required: true, options: dropdownOptions.paymentMode },
          { label: t(`fields.orders.comment`), name: "comment", type: "textarea" },
        ];
      case "quotes":
        return [
          { label: t(`fields.orders.clientId`), name: "clientId", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`fields.orders.repoId`), name: "repoId", type: "dropdown", required: true, options: dropdownOptions.repos },
          { label: t(`fields.orders.paymentMode`), name: "paymentMode", type: "dropdown", required: false, options: dropdownOptions.paymentMode },
        ];
      case "purchases":
        return [
          { label: t(`fields.payments.date_facture`), name: "dateFacture", type: "date", required: true },
          { label: t(`fields.purchases.factureNumber`), name: "factureNumber", required: true },
          { label: t(`fields.purchases.fournisseur`), name: "fournisseur", type: "dropdown", options: dropdownOptions.providers, required: true },
          { label: t(`fields.purchases.stockAlimenter`), name: "stockAlimenter", type: "dropdown", options: dropdownOptions.repos, required: true },
        ];
      case "users":
        return [
          { label: t(`fields.users.firstName`), name: "firstName", required: true },
          { label: t(`fields.users.lastName`), name: "lastName", required: true },
          { label: t(`fields.users.username`), name: "username", required: true },
          { label: t(`fields.users.email`), name: "email", required: true },
          {
            label: t(`fields.users.role`), name: "role", type: "dropdown", required: true,
            options: dropdownOptions.roles,
            conditionalFields: {
              ROLE_COMMERCIAL: [
                { name: "repositories", label: t('fields.users.repositories'), type: 'checkbox-dropdown', options: dropdownOptions.repos },
              ],
            },
          },
          { label: t(`fields.users.password`), name: "password", type: "password" },
          { label: t(`fields.users.passwordConfirmation`), name: "passwordConfirmation", type: "password", required: true, isPasswordConfirmation: true },
        ];
      case "repos":
        return [
          { label: t(`fields.repos.name`), name: "name", required: true },
        ];
      case "providers":
        return [
          { label: t(`fields.providers.name`), name: "name", required: true },
        ];
      case "userobj":
        return [
          { label: t(`fields.userobj.objectif`), name: "objectif", required: true },
        ];
      case "requests":
        return [
          { label: t(`fields.requests.sourceRepo`), name: "sourceRepo", type: "dropdown", required: true, options: dropdownOptions.sourceRepos },
          { label: t(`fields.requests.destRepo`), name: "destRepo", type: "dropdown", required: true, options: dropdownOptions.destRepos },
        ];
      case "profile":
        return [
          { label: t(`fields.users.lastName`), name: "lastName" },
          { label: t(`fields.users.firstName`), name: "firstName" },
          {
            label: t(`fields.users.language`), name: "preferredLanguage", type: "dropdown",
            options: [
              { label: t(`lang.fr`), value: "fr" },
              { label: t(`lang.en`), value: "en" }
            ],
            required: true
          },
        ];
      case "settings":
        return [
          { label: t(`fields.settings.grayName`), name: "companyName", required: true },
          { label: t(`fields.settings.fname`), name: "contactFirstName", required: true },
          { label: t(`fields.settings.lname`), name: "contactLastName", required: true },
          { label: t(`fields.settings.contactEmail`), name: "contactEmail" },
          { label: t(`fields.settings.phoneNumber`), name: "phoneNumber", required: true },
          {
            label: t(`fields.users.language`), name: "language", type: "dropdown",
            options: [
              { label: t(`lang.fr`), value: "fr" },
              { label: t(`lang.en`), value: "en" }
            ],
          },
          {
            label: t(`fields.settings.footer`),
            name: "footer",
            type: "text",
            isNormal: true
          },
        ];
      case "creditNotes":
        return [
          { label: t(`fields.orders.clientId`), name: "client", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`fields.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        ]
      case "payments":
        return [
          { label: t(`fields.payments.clientId`), name: "clientId", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`fields.payments.paymentType`), name: "paymentTypeId", type: "dropdown", required: true, options: dropdownOptions.paymentMode },
          { label: t(`fields.payments.bank`), name: "bankId", type: "dropdown", options: dropdownOptions.banks },
          { label: t(`fields.payments.number`), name: "reference", type: "text", required: true },
          { label: t(`fields.payments.amount`), name: "amount" },
          { label: t(`fields.payments.date_echeance`), name: "echeance", type: "date" },
          { label: t(`fields.payments.date_facture`), name: "facture", type: "date" },
          { label: t(`fields.payments.comment`), name: "note", type: "text" },
        ];
      case "expenses":
        return [
          { label: "Notes", name: "comment", type: "textarea", required: false },
        ];
      case "afinancier":
        return [
          { label: t(`fields.payments.clientId`), name: "clientId", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`columns.commercial`), name: "commercialId", type: "dropdown", required: true, options: dropdownOptions.commercial },
        ];
      case "afournisseur":
        return [
          { label: t(`fields.purchases.fournisseur`), name: "provider", type: "dropdown", options: dropdownOptions.providers, required: true },
          { label: t(`rows.repo`), name: "repo", type: "dropdown", required: true, options: dropdownOptions.sourceRepos },
        ];
      case "banks": 
        return [
          { label: t(`fields.banks.name`), name: "name", type: "text", required: true },
        ];
      case "paymentTypes":
        return [
          { label: t(`fields.paymentTypes.name`), name: "name", type: "text", required: true },
        ];
      default:
        return [];
    }
  };
  const getListFieldSpecs = () => {
    if (moduleType === "orders") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } if (moduleType === "quotes") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } else if (moduleType === "purchases") {
      return [
        { label: t(`fields.list.product`), name: "productName", type: "dropdown", options: dropdownOptions.products, required: true },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
        { label: t(`fields.list.price`), name: "unitPrice", required: true },
      ];
    } else if (moduleType === "requests") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", options: dropdownOptions.products, required: true },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } else if (moduleType === "expenses") {
      return [
        { label: t(`rows.category`), name: "category", type: "dropdown", options: dropdownOptions.expense_category, required: true },
        { label: t(`fields.payments.amount`), name: "price", required: true },
        { label: t(`fields.payments.other`), name: "comment", type: "text", required: false },
      ];
    } else if (moduleType === "afinancier") {
      return [
        { label: t(`rows.designation`), name: "designation", type: "text", required: true },
        { label: t(`fields.products.priceTTC`), name: "priceTTC", required: true },
      ];
    } else if (moduleType === "afournisseur") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
      ];
    }
    return [];
  };

  const getRedirectUrl = () => {
    switch (moduleType) {
      case "brands":
        return "/catalog/brands";
      case "families":
        return "/catalog/families";
      case "orders":
        return "/orders";
      case "purchases":
        return "/purchases";
      case "users":
        return "/users";
      case "userobj":
        return "/users";
      case "repos":
        return "/repos";
      case "providers":
        return "/providers";
      case "products":
        return "/catalog/products";
      case "clients":
        return "/clients";
      case "requests":
        return "/transfer-requests";
      case "profile":
        return "/";
      case "settings":
        return "/";
      case "creditNotes":
        return "/credit-notes";
      case "quotes":
        return "/quotes";
      case "payments":
        return "/payments";
      case "afournisseur":
        return "/avoirs-fournisseurs";
      case "afinancier":
        return "/avoirs-financiers";
      case "invoices":
        return "/invoices";
      case "expenses":
        return "/expenses";
      case "banks":
        return "/banks";
      case "paymentTypes":
        return "/payment-types";
      default:
        return "/";
    }
  };

  const redirectUrl = getRedirectUrl();

  let fields = getFieldSpecs().map((field) => ({
    ...field,
    value: mode === "modify" ? initialValues[field.name] : "",
  }));

  if (mode === "modify" && moduleType === "users") {
    fields = fields.filter(field =>
      field.name !== "password" &&
      field.name !== "passwordConfirmation" &&
      field.name !== "username"
    );
  }
  if (mode === "modify" && initialValues.id && moduleType !== "afinancier") {
    fields.push({ name: "id", value: initialValues.id, type: "hidden" });
  }

  const listFields = getListFieldSpecs().map((field) => ({
    ...field,
    value: mode === "modify" ? initialValues[field.name] : "",
  }));

  let fileFields = [];

  switch (moduleType) {
    case "brands":
    case "families":
      fileFields = [{
        name: "image",
        label: "",
        accept: "image/*",
        icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
        placeholderText1: t("placeholder.upload_logo1"),
        placeholderText2: t("placeholder.upload_logo2")
      }];
      break;
    case "settings":
      fileFields = [
        {
          name: "image",
          label: t("fields.logoImage"),
          accept: "image/*",
          icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
          placeholderText1: t("placeholder.upload_logo1"),
          placeholderText2: t("placeholder.upload_logo2")
        },
        {
          name: "header",
          label: t("fields.headerImage"),
          accept: "image/*",
          icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
          placeholderText1: t("placeholder.upload_header"),
          placeholderText2: t("placeholder.upload_logo2")
        }
      ];
      break;
    case "payments":
      fileFields = [
        {
          name: "image",
          label: t("fields.paymentImage"),
          accept: "image/*",
          placeholderText1: t("placeholder.upload_logo1"),
          placeholderText2: t("placeholder.upload_logo2")
        }
      ];
      break;
    default:
      fileFields = [];
  }

  const productFields = getProductFormFields();
  const clientFields = getClientFormFields();

  const apiUrl = moduleType === "paymentTypes" ? `/payments/types/add` : `${moduleType}/add`;

  const handleFormSubmit = async (responseData) => {
    if (!onClose) {
      history.push(redirectUrl);
    }
  };

  const handleSuccess = () => {
    const message = mode === 'modify'
      ? t(`confirmation.${moduleType}Edit`)
      : t(`confirmation.${moduleType}Add`);
    if (onSuccess) onSuccess(message);
    if (onClose) {
      onClose("submit");
    } else {
      history.push({
        pathname: redirectUrl,
        state: {
          showSnackbar: true,
          snackbarMessage: message,
          snackbarSeverity: 'success'
        }
      });
    }
  };

  const handleCancel = () => {

    if (onClose) {
      onClose("cancel");
    } else {
      history.push(redirectUrl);
    }
  };

  const renderPopup = () => {


    const formTitle = getFormTitle();
    if (moduleType === "brands" || moduleType === "families" || moduleType === "users" || moduleType === "repos" || moduleType === "providers" || moduleType === "userobj" || moduleType === "profile" || moduleType === "template" || moduleType === "settings" || moduleType === "banks" || moduleType === "paymentTypes") {
      return (
        <DynamicForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={fields}
          additionalFields={additionalFields}
          listFields={listFields}
          products={dropdownOptions.products}
          fileFields={fileFields}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          apiUrl={apiUrl}
          module={moduleType}

        />
      )
    } else if (moduleType === "products") {
      return (
        <ProductForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={productFields.baseFields}
          familyAndBrandFields={productFields.familyAndBrandFields}
          quantityFields={productFields.quantityFields}
          additionalFields={additionalFields}
          fileFields={productFields.fileFields}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          apiUrl={apiUrl}
        />
      );
    }
  };

  const renderForm = () => {

    const formTitle = getFormTitle();


    if (moduleType === "clients") {
      return (
        <ClientForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={clientFields.baseFields}
          additionalFields={additionalFields}
          paymentMode={dropdownOptions.paymentMode}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === "purchases") {
      return (
        <PurchaseForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          additionalFields={additionalFields}
          products={dropdownOptions.products}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      )
    } else if (moduleType === "orders") {
      return (
        <OrderForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          additionalFields={additionalFields}
          products={dropdownOptions.products}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === "quotes") {
      return (
        <QuoteForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          additionalFields={additionalFields}
          products={dropdownOptions.products}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === "requests") {
      return (
        <RequestForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={fields}
          listFields={listFields}
          additionalFields={additionalFields}
          products={dropdownOptions.products}
          onSuccess={handleSuccess}
        />
      );
    } else if (moduleType === 'creditNotes') {
      return (
        <CreditNoteForm
          title={formTitle}
          baseFields={fields}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === 'expenses') {
      return (
        <ExpenseForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === 'payments') {
      return (
        <PaymentsForm
          title={formTitle}
          baseFields={fields}
          fileFields={fileFields}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === 'afournisseur') {
      return (
        <AvoirFournisseurForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          products={dropdownOptions.products}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === 'afinancier') {
      return (
        <AvoirFinancierForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          initialValues={initialValues}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    }
  };


  return (
    <div>
      {renderPopup()}
      <div>{renderForm()}</div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );

}

export default ModuleWrapper;
